import logger from '@/lib/logger';
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { User, SignOut, Download, Heart, Bell, BellSlash, GoogleLogo, Brain, Flame, Crown, Lock, Trash } from '@phosphor-icons/react';
import {
  isPushSupported, getPushPermission, subscribeToPush, unsubscribeFromPush, sendTestPush, getPushStatus, getSuggestedHour, getUserTimezone,
} from '@/lib/push';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STREAK_SAVE_MIN_DAYS_DISPLAY = 3;

export default function ProfilePage() {
  const { user, logout, updateUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [level, setLevel] = useState(user?.fitness_level || 'beginner');
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState(null);

  // Push notifications state
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [reminderHour, setReminderHour] = useState(18);
  const [smartReminder, setSmartReminder] = useState(false);
  const [streakSaveEnabled, setStreakSaveEnabled] = useState(true);
  const [suggestedHour, setSuggestedHour] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState(null);
  const pushSupported = isPushSupported();
  const userTz = getUserTimezone();
  const isPro = !!user?.is_pro;

  // Google Fit state
  const [gfitStatus, setGfitStatus] = useState(null);

  // Security state
  const [showPwForm, setShowPwForm] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  const [showDeleteForm, setShowDeleteForm] = useState(false);
  const [deletePw, setDeletePw] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState(null);

  const submitPwChange = async (e) => {
    e.preventDefault();
    setPwBusy(true); setPwMsg(null);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPw, new_password: newPw });
      setPwMsg('Password changed successfully.');
      setCurrentPw(''); setNewPw('');
      setTimeout(() => { setShowPwForm(false); setPwMsg(null); }, 1500);
    } catch (err) {
      setPwMsg(err?.response?.data?.detail || 'Could not change password.');
    } finally { setPwBusy(false); }
  };

  const submitDelete = async (e) => {
    e.preventDefault();
    if (deleteConfirm !== 'DELETE') return;
    setDeleteBusy(true); setDeleteMsg(null);
    try {
      await axios.delete(`${API}/auth/account`, { data: { password: deletePw, confirmation: 'DELETE' } });
      await logout();
      window.location.href = '/';
    } catch (err) {
      setDeleteMsg(err?.response?.data?.detail || 'Could not delete account.');
      setDeleteBusy(false);
    }
  };

  useEffect(() => {
    const loadStatuses = async () => {
      if (pushSupported) {
        try {
          setPushPermission(await getPushPermission());
          const s = await getPushStatus();
          setPushEnabled(s.enabled);
          if (s.subscriptions?.[0]) {
            if (s.subscriptions[0].reminder_hour != null) setReminderHour(s.subscriptions[0].reminder_hour);
            setSmartReminder(!!s.subscriptions[0].smart_reminder);
            if (s.subscriptions[0].streak_save_enabled != null) {
              setStreakSaveEnabled(!!s.subscriptions[0].streak_save_enabled);
            }
          }
          const sh = await getSuggestedHour();
          setSuggestedHour(sh.suggested_hour);
        } catch (err) { logger.warn('push status err', err?.message); }
      }
      try {
        const resp = await axios.get(`${API}/integrations/google-fit/status`);
        setGfitStatus(resp.data);
      } catch (err) { logger.warn('gfit status err', err?.message); }
    };
    loadStatuses();
  }, [pushSupported]);

  const handlePushToggle = async () => {
    setPushBusy(true); setPushMsg(null);
    try {
      if (pushEnabled) {
        await unsubscribeFromPush();
        setPushEnabled(false);
        setPushMsg('Notifications disabled.');
      } else {
        await subscribeToPush(reminderHour, smartReminder, streakSaveEnabled);
        setPushEnabled(true);
        setPushPermission('granted');
        setPushMsg(
          smartReminder
            ? 'Smart reminder enabled — GRIT.FIT will learn your workout time.'
            : `Daily reminders set for ${reminderHour.toString().padStart(2, '0')}:00 (${userTz}).`
        );
      }
    } catch (err) {
      logger.error('push toggle', err);
      setPushMsg(err?.message || 'Failed to toggle notifications.');
    } finally { setPushBusy(false); }
  };

  const handlePushTest = async () => {
    setPushBusy(true); setPushMsg(null);
    try {
      const data = await sendTestPush();
      setPushMsg(`Test push sent to ${data.sent} device${data.sent === 1 ? '' : 's'}.`);
    } catch (err) {
      setPushMsg(err?.response?.data?.detail || 'Test push failed.');
    } finally { setPushBusy(false); }
  };

  const handleReminderHourChange = async (newHour) => {
    setReminderHour(newHour);
    if (pushEnabled) {
      setPushBusy(true);
      try {
        await subscribeToPush(newHour, smartReminder, streakSaveEnabled);
        setPushMsg(`Reminder time updated to ${newHour.toString().padStart(2, '0')}:00 (${userTz}).`);
      } catch (err) { logger.error(err); }
      finally { setPushBusy(false); }
    }
  };

  const handleSmartToggle = async () => {
    const next = !smartReminder;
    setSmartReminder(next);
    if (pushEnabled) {
      setPushBusy(true); setPushMsg(null);
      try {
        await subscribeToPush(reminderHour, next, streakSaveEnabled);
        setPushMsg(
          next
            ? (suggestedHour != null
                ? `Smart mode ON — reminders at ${suggestedHour.toString().padStart(2, '0')}:00 (${userTz}, your median)`
                : 'Smart mode ON — needs 3+ workouts to learn your timing')
            : `Smart mode OFF — reminders at ${reminderHour.toString().padStart(2, '0')}:00 (${userTz}).`
        );
      } catch (err) { logger.error(err); }
      finally { setPushBusy(false); }
    }
  };

  const handleStreakSaveToggle = async () => {
    const next = !streakSaveEnabled;
    setStreakSaveEnabled(next);
    if (pushEnabled) {
      setPushBusy(true); setPushMsg(null);
      try {
        await subscribeToPush(reminderHour, smartReminder, next);
        setPushMsg(next ? 'Streak-save alerts ON.' : 'Streak-save alerts OFF.');
      } catch (err) { logger.error(err); }
      finally { setPushBusy(false); }
    }
  };

  const handleGfitConnect = async () => {
    try {
      const { data } = await axios.get(`${API}/integrations/google-fit/connect`);
      if (data.auth_url) window.location.href = data.auth_url;
    } catch (err) {
      setPushMsg(err?.response?.data?.detail || 'Google Fit not configured yet.');
    }
  };

  const exportData = async (format) => {
    setExporting(true);
    setExportMsg(null);
    try {
      const { data } = await axios.get(`${API}/workouts/history`);
      const workouts = Array.isArray(data) ? data : (data?.workouts || []);
      let blob;
      let filename;
      if (format === 'csv') {
        const headers = ['date', 'workout_id', 'duration_minutes', 'exercises_completed'];
        const rows = workouts.map(w => [
          w.completed_at || w.created_at || '',
          w.workout_id || '',
          w.duration_minutes || 0,
          w.exercises_completed || 0,
        ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
        const csv = [headers.join(','), ...rows].join('\n');
        blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        filename = `nogear-workouts-${new Date().toISOString().split('T')[0]}.csv`;
      } else {
        const payload = {
          exported_at: new Date().toISOString(),
          user: { name: user?.name, email: user?.email, fitness_level: user?.fitness_level },
          workouts,
        };
        blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        filename = `nogear-workouts-${new Date().toISOString().split('T')[0]}.json`;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportMsg(`Exported ${workouts.length} workout${workouts.length === 1 ? '' : 's'} as ${format.toUpperCase()}`);
    } catch (err) {
      logger.error('Export error:', err);
      setExportMsg('Export failed. Try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/user/onboarding`, {
        fitness_level: level,
        goals: user?.goals || [],
        body_focus: user?.body_focus || [],
        dietary_preferences: user?.dietary_preferences || 'balanced',
      });
      updateUser(data);
      setEditing(false);
    } catch (err) {
      logger.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="profile-page">
      <div className="mb-8">
        <div className="label-tactical text-[#FF3B30] mb-1">Settings</div>
        <h1 className="text-4xl font-black tracking-tighter">PROFILE</h1>
      </div>

      <div className="max-w-2xl space-y-4">
        {/* User Info */}
        <div className="tactical-card p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-[#FF3B30]/20 flex items-center justify-center">
              <User weight="bold" className="w-8 h-8 text-[#FF3B30]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight">{user?.name}</h2>
              <p className="text-[#A1A1AA]">{user?.email}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#1C1C1E] p-4 border border-white/5">
              <div className="label-tactical text-[#FF3B30] mb-1">Fitness Level</div>
              {editing ? (
                <select value={level} onChange={(e) => setLevel(e.target.value)}
                  className="bg-transparent text-white border-b border-white/20 outline-none w-full uppercase font-bold">
                  <option value="beginner" className="bg-[#141414]">Beginner</option>
                  <option value="intermediate" className="bg-[#141414]">Intermediate</option>
                  <option value="advanced" className="bg-[#141414]">Advanced</option>
                </select>
              ) : (
                <p className="font-bold uppercase">{user?.fitness_level}</p>
              )}
            </div>
            <div className="bg-[#1C1C1E] p-4 border border-white/5">
              <div className="label-tactical text-[#007AFF] mb-1">Diet</div>
              <p className="font-bold uppercase">{user?.dietary_preferences?.replace('_', ' ')}</p>
            </div>
          </div>

          {/* Goals */}
          <div className="mt-4">
            <div className="label-tactical mb-2">Goals</div>
            <div className="flex flex-wrap gap-2">
              {(user?.goals || []).map(g => (
                <span key={g} className="px-3 py-1 text-xs font-bold uppercase tracking-wider bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/20">
                  {g.replace('_', ' ')}
                </span>
              ))}
            </div>
          </div>

          {/* Body Focus */}
          <div className="mt-4">
            <div className="label-tactical mb-2">Focus Areas</div>
            <div className="flex flex-wrap gap-2">
              {(user?.body_focus || []).map(b => (
                <span key={b} className="px-3 py-1 text-xs font-bold uppercase tracking-wider bg-[#007AFF]/10 text-[#007AFF] border border-[#007AFF]/20">
                  {b.replace('_', ' ')}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            {editing ? (
              <>
                <button onClick={handleSave} disabled={saving} className="btn-primary px-6 py-2.5 text-sm" data-testid="save-profile-button">
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button onClick={() => setEditing(false)} className="btn-secondary px-6 py-2.5 text-sm">Cancel</button>
              </>
            ) : (
              <button onClick={() => setEditing(true)} className="btn-secondary px-6 py-2.5 text-sm" data-testid="edit-profile-button">
                Edit Profile
              </button>
            )}
          </div>
        </div>

        {/* Push Notifications */}
        <div className="tactical-card p-6" data-testid="push-card">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 flex items-center justify-center ${pushEnabled ? 'bg-[#FF3B30]/10' : 'bg-[#71717A]/10'}`}>
              {pushEnabled
                ? <Bell weight="fill" className="w-5 h-5 text-[#FF3B30]" />
                : <BellSlash weight="fill" className="w-5 h-5 text-[#71717A]" />}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold tracking-tight">Daily Workout Reminder</h3>
              <p className="text-xs text-[#71717A]">
                {pushSupported
                  ? 'Get a push notification to build the habit'
                  : 'Not supported in this browser — try Chrome or install as PWA'}
              </p>
            </div>
          </div>

          {pushSupported ? (
            <>
              {/* Smart Reminder toggle (Pro) */}
              <div
                className={`flex items-start gap-3 p-3 mb-4 border ${smartReminder ? 'border-[#FF3B30]/40 bg-[#FF3B30]/5' : 'border-white/5 bg-[#1C1C1E]'} ${!isPro ? 'opacity-60' : ''}`}
                data-testid="smart-reminder-block"
              >
                <Brain weight="fill" className={`w-5 h-5 mt-0.5 ${smartReminder ? 'text-[#FF3B30]' : 'text-[#71717A]'}`} />
                <div className="flex-1">
                  <label className="flex items-center gap-2 cursor-pointer flex-wrap">
                    <input
                      type="checkbox"
                      checked={smartReminder}
                      onChange={handleSmartToggle}
                      disabled={pushBusy || !isPro}
                      data-testid="smart-reminder-checkbox"
                      className="w-4 h-4 accent-[#FF3B30]"
                    />
                    <span className="text-sm font-bold tracking-tight">Smart mode (learn my workout time)</span>
                    {!isPro && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-[#FF9500]/15 text-[#FF9500] border border-[#FF9500]/30" data-testid="smart-pro-badge">
                        <Crown weight="fill" className="w-3 h-3" /> Pro
                      </span>
                    )}
                  </label>
                  <p className="text-xs text-[#A1A1AA] mt-1">
                    {!isPro
                      ? <>Upgrade to Pro to let GRIT.FIT learn your median workout hour automatically. <a href="/pricing" className="text-[#FF9500] font-bold underline" data-testid="smart-upgrade-link">Upgrade →</a></>
                      : smartReminder
                        ? (suggestedHour != null
                            ? <>Auto-firing at <span className="text-[#FF3B30] font-bold">{suggestedHour.toString().padStart(2, '0')}:00</span> <span className="text-[#71717A]">({userTz})</span> — the median hour of your last 14 workouts.</>
                            : <>Needs 3+ completed workouts to start learning. Using <span className="text-[#FF3B30] font-bold">{reminderHour.toString().padStart(2, '0')}:00</span> as fallback.</>)
                        : 'Disabled — uses the manual hour below.'}
                  </p>
                </div>
              </div>

              <div className={`flex flex-wrap items-center gap-3 mb-4 ${smartReminder ? 'opacity-40 pointer-events-none' : ''}`}>
                <label htmlFor="reminder-hour" className="label-tactical text-[#A1A1AA]">Reminder Hour</label>
                <select
                  id="reminder-hour"
                  value={reminderHour}
                  onChange={(e) => handleReminderHourChange(Number(e.target.value))}
                  disabled={pushBusy || smartReminder}
                  data-testid="reminder-hour-select"
                  className="bg-[#1C1C1E] text-white border border-white/10 px-3 py-2 text-sm font-bold outline-none"
                >
                  {Array.from({ length: 24 }).map((_, h) => (
                    <option key={h} value={h}>{h.toString().padStart(2, '0')}:00</option>
                  ))}
                </select>
                <span className="text-xs text-[#71717A]" data-testid="reminder-tz-label">
                  your local time · {userTz}
                </span>
              </div>

              {/* Streak-save toggle (Pro) */}
              <div
                className={`flex items-start gap-3 p-3 mb-4 border ${streakSaveEnabled ? 'border-[#FF9500]/40 bg-[#FF9500]/5' : 'border-white/5 bg-[#1C1C1E]'} ${!isPro ? 'opacity-60' : ''}`}
                data-testid="streak-save-block"
              >
                <Flame weight="fill" className={`w-5 h-5 mt-0.5 ${streakSaveEnabled ? 'text-[#FF9500]' : 'text-[#71717A]'}`} />
                <div className="flex-1">
                  <label className="flex items-center gap-2 cursor-pointer flex-wrap">
                    <input
                      type="checkbox"
                      checked={streakSaveEnabled}
                      onChange={handleStreakSaveToggle}
                      disabled={pushBusy || !isPro}
                      data-testid="streak-save-checkbox"
                      className="w-4 h-4 accent-[#FF9500]"
                    />
                    <span className="text-sm font-bold tracking-tight">Streak-save alerts</span>
                    {!isPro && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-[#FF9500]/15 text-[#FF9500] border border-[#FF9500]/30" data-testid="streak-pro-badge">
                        <Crown weight="fill" className="w-3 h-3" /> Pro
                      </span>
                    )}
                  </label>
                  <p className="text-xs text-[#A1A1AA] mt-1">
                    {!isPro
                      ? <>Never break a streak again. Pro sends a 22:00 last-chance nudge when your {STREAK_SAVE_MIN_DAYS_DISPLAY}+ day streak is at risk. <a href="/pricing" className="text-[#FF9500] font-bold underline" data-testid="streak-upgrade-link">Upgrade →</a></>
                      : streakSaveEnabled
                        ? <>Urgent nudge at <span className="text-[#FF9500] font-bold">22:00</span> ({userTz}) if your {STREAK_SAVE_MIN_DAYS_DISPLAY}+ day streak is about to break and you haven't trained yet.</>
                        : 'Disabled — no last-minute streak-save reminders.'}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handlePushToggle}
                  disabled={pushBusy || pushPermission === 'denied'}
                  className={`${pushEnabled ? 'btn-secondary' : 'btn-primary'} px-5 py-2.5 text-sm flex items-center gap-2 disabled:opacity-50`}
                  data-testid="push-toggle-button"
                >
                  {pushBusy ? '...' : pushEnabled ? <><BellSlash weight="bold" className="w-4 h-4" /> Disable</> : <><Bell weight="bold" className="w-4 h-4" /> Enable Reminders</>}
                </button>
                {pushEnabled && (
                  <button
                    onClick={handlePushTest}
                    disabled={pushBusy}
                    className="btn-secondary px-5 py-2.5 text-sm disabled:opacity-50"
                    data-testid="push-test-button"
                  >
                    Send Test
                  </button>
                )}
              </div>

              {pushPermission === 'denied' && (
                <p className="mt-3 text-xs text-[#FF9500]">
                  Notifications blocked. Re-enable in your browser site settings to turn this on.
                </p>
              )}
              {pushMsg && <p className="mt-3 text-xs text-[#34C759]" data-testid="push-status">{pushMsg}</p>}
            </>
          ) : (
            <p className="text-sm text-[#71717A]">
              Install GRIT.FIT as a PWA on your device to enable push notifications.
            </p>
          )}
        </div>

        {/* Google Fit */}
        <div className="tactical-card p-6" data-testid="gfit-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#4285F4]/10 flex items-center justify-center">
              <GoogleLogo weight="bold" className="w-5 h-5 text-[#4285F4]" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold tracking-tight">Google Fit</h3>
              <p className="text-xs text-[#71717A]">
                {gfitStatus?.connected
                  ? `Connected · last sync ${gfitStatus.last_sync || '—'}`
                  : gfitStatus?.configured
                  ? 'Sync your workouts to Google Fit'
                  : 'Integration available — awaiting OAuth keys'}
              </p>
            </div>
          </div>
          {gfitStatus?.connected ? (
            <button className="btn-secondary px-5 py-2.5 text-sm" data-testid="gfit-disconnect-button">
              Disconnect
            </button>
          ) : gfitStatus?.configured ? (
            <button
              onClick={handleGfitConnect}
              className="btn-primary px-5 py-2.5 text-sm flex items-center gap-2"
              data-testid="gfit-connect-button"
            >
              <GoogleLogo weight="bold" className="w-4 h-4" /> Connect Google Fit
            </button>
          ) : (
            <div className="text-xs text-[#A1A1AA] bg-[#1C1C1E] border border-white/5 p-3 leading-relaxed">
              {gfitStatus?.setup_instructions || 'Google Fit OAuth not configured.'}
              <br />
              <span className="text-[#71717A]">Until then, use the CSV/JSON export below with <span className="text-[#007AFF]">Health Sync</span> or <span className="text-[#007AFF]">Health Auto Export</span>.</span>
            </div>
          )}
        </div>

        {/* Health Data Export */}
        <div className="tactical-card p-6 mb-20 lg:mb-0" data-testid="health-export-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#34C759]/10 flex items-center justify-center">
              <Heart weight="fill" className="w-5 h-5 text-[#34C759]" />
            </div>
            <div>
              <h3 className="text-lg font-bold tracking-tight">Export Workout Data</h3>
              <p className="text-xs text-[#71717A]">Sync with Apple Health / Google Fit via CSV or JSON</p>
            </div>
          </div>
          <p className="text-sm text-[#A1A1AA] mb-4">
            Download your complete workout history. Use <span className="text-[#34C759]">CSV</span> for spreadsheets,{' '}
            <span className="text-[#007AFF]">JSON</span> for apps like Health Auto Export (iOS) or FitNotes (Android).
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => exportData('csv')}
              disabled={exporting}
              className="btn-primary px-5 py-2.5 text-sm flex items-center gap-2 disabled:opacity-50"
              data-testid="export-csv-button"
            >
              <Download weight="bold" className="w-4 h-4" /> {exporting ? 'Exporting...' : 'Download CSV'}
            </button>
            <button
              onClick={() => exportData('json')}
              disabled={exporting}
              className="btn-secondary px-5 py-2.5 text-sm flex items-center gap-2 disabled:opacity-50"
              data-testid="export-json-button"
            >
              <Download weight="bold" className="w-4 h-4" /> {exporting ? 'Exporting...' : 'Download JSON'}
            </button>
          </div>
          {exportMsg && (
            <p className="mt-3 text-xs text-[#34C759]" data-testid="export-status">{exportMsg}</p>
          )}
        </div>

        {/* Security — Change password */}
        <div className="tactical-card p-6" data-testid="security-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#007AFF]/10 flex items-center justify-center">
              <Lock weight="fill" className="w-5 h-5 text-[#007AFF]" />
            </div>
            <div>
              <h3 className="text-lg font-bold tracking-tight">Security</h3>
              <p className="text-xs text-[#71717A]">Change password or delete account</p>
            </div>
          </div>
          {!showPwForm ? (
            <button onClick={() => setShowPwForm(true)} className="btn-secondary px-5 py-2.5 text-sm" data-testid="open-change-password">
              Change Password
            </button>
          ) : (
            <form onSubmit={submitPwChange} className="space-y-3">
              <input
                type="password"
                placeholder="Current password"
                value={currentPw}
                onChange={e => setCurrentPw(e.target.value)}
                required
                data-testid="current-password-input"
                className="w-full bg-[#1C1C1E] border border-white/10 text-white px-4 py-2.5 outline-none focus:border-[#007AFF] text-sm"
              />
              <input
                type="password"
                placeholder="New password (min 8 chars)"
                value={newPw}
                onChange={e => setNewPw(e.target.value)}
                required
                minLength={8}
                data-testid="new-password-input"
                className="w-full bg-[#1C1C1E] border border-white/10 text-white px-4 py-2.5 outline-none focus:border-[#007AFF] text-sm"
              />
              <div className="flex gap-2">
                <button type="submit" disabled={pwBusy} className="btn-primary px-5 py-2.5 text-sm disabled:opacity-50" data-testid="submit-change-password">
                  {pwBusy ? 'Saving...' : 'Save'}
                </button>
                <button type="button" onClick={() => { setShowPwForm(false); setCurrentPw(''); setNewPw(''); setPwMsg(null); }} className="btn-secondary px-5 py-2.5 text-sm">
                  Cancel
                </button>
              </div>
              {pwMsg && <p className={`text-xs ${pwMsg.startsWith('Password') ? 'text-[#34C759]' : 'text-[#FF3B30]'}`} data-testid="change-password-msg">{pwMsg}</p>}
            </form>
          )}
        </div>

        {/* Danger zone — Delete account */}
        {user?.role !== 'admin' && (
          <div className="tactical-card p-6 border-[#FF3B30]/30" data-testid="danger-zone">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#FF3B30]/10 flex items-center justify-center">
                <Trash weight="fill" className="w-5 h-5 text-[#FF3B30]" />
              </div>
              <div>
                <h3 className="text-lg font-bold tracking-tight text-[#FF3B30]">Danger Zone</h3>
                <p className="text-xs text-[#71717A]">Permanently delete your account and all data</p>
              </div>
            </div>
            {!showDeleteForm ? (
              <button onClick={() => setShowDeleteForm(true)} className="btn-secondary px-5 py-2.5 text-sm border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10" data-testid="open-delete-account">
                Delete my account
              </button>
            ) : (
              <form onSubmit={submitDelete} className="space-y-3">
                <p className="text-xs text-[#A1A1AA]">
                  This will permanently delete your account, workouts, streak, mood logs, buddy connections, and push subscriptions. <span className="text-[#FF3B30] font-bold">This cannot be undone.</span>
                </p>
                <input
                  type="password"
                  placeholder="Your password"
                  value={deletePw}
                  onChange={e => setDeletePw(e.target.value)}
                  required
                  data-testid="delete-password-input"
                  className="w-full bg-[#1C1C1E] border border-white/10 text-white px-4 py-2.5 outline-none focus:border-[#FF3B30] text-sm"
                />
                <input
                  type="text"
                  placeholder="Type DELETE to confirm"
                  value={deleteConfirm}
                  onChange={e => setDeleteConfirm(e.target.value)}
                  required
                  data-testid="delete-confirm-input"
                  className="w-full bg-[#1C1C1E] border border-white/10 text-white px-4 py-2.5 outline-none focus:border-[#FF3B30] text-sm uppercase tracking-wider"
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={deleteBusy || deleteConfirm !== 'DELETE'}
                    className="btn-primary bg-[#FF3B30] hover:bg-[#FF645A] px-5 py-2.5 text-sm disabled:opacity-30"
                    data-testid="submit-delete-account"
                  >
                    {deleteBusy ? 'Deleting...' : 'Permanently Delete'}
                  </button>
                  <button type="button" onClick={() => { setShowDeleteForm(false); setDeletePw(''); setDeleteConfirm(''); setDeleteMsg(null); }} className="btn-secondary px-5 py-2.5 text-sm">
                    Cancel
                  </button>
                </div>
                {deleteMsg && <p className="text-xs text-[#FF3B30]" data-testid="delete-account-msg">{deleteMsg}</p>}
              </form>
            )}
          </div>
        )}

        {/* Logout */}
        <button onClick={logout} className="flex items-center gap-2 text-[#71717A] hover:text-[#FF3B30] transition-colors" data-testid="profile-logout-button">
          <SignOut className="w-5 h-5" /> Sign Out
        </button>
      </div>
    </div>
  );
}
